import { useLayoutEffect, useState } from 'react';

// Which side of its field a booking-bar panel (a field dropdown, or the
// date-range calendar) opens on.
//
// Down is the design and stays the default -- this flips a panel up whenever
// it wouldn't fit below the field in the VIEWPORT, which is how date pickers
// behave generally (flatpickr, react-select). Note the deliberate choice: an
// in-flow bar's panel could technically be scrolled into view, so a stricter
// rule would only flip a bottom-pinned bar. Fitting on screen won, because a
// panel that opens half below the fold reads as broken whether or not the
// page can be scrolled -- so the homepage hero's fields, which sit low in a
// full-height hero, open upward at scroll zero.
//
// Both of the situations that need this are covered by the one measurement,
// and neither is named here: a bar pinned to the bottom of the screen
// (.is-pinned, which used to carry a hardcoded flip of its own), and an
// ordinary bar sitting low in the window. It also keeps working if the pinned
// bar's offset or a panel's height ever changes.
export type FlipPlacement = 'down' | 'up';

// The panel inside a field. Both kinds are positioned identically (top:100% +
// --ocean-dropdown-offset, see booking-bar.scss) so both flip the same way.
const PANEL_SELECTOR = '.booking-bar__dropdown, .booking-bar__calendar';

// Deliberately a loose shape rather than React's RefObject, whose null-ness
// in the type moved between React 18 and 19 -- this only ever reads .current.
interface FieldRef
	{
	current: HTMLElement | null;
	}

export const useFlipPlacement = (fieldRef: FieldRef, isOpen: boolean): FlipPlacement =>
	{
	const [placement, setPlacement] = useState<FlipPlacement>('down');

	// Layout effect, not a plain effect: the panel is measured and the class
	// applied before the browser paints, so a panel that needs flipping is
	// never briefly visible hanging off the bottom of the screen.
	useLayoutEffect(() => {
		if (!isOpen)
			{
			// Back to the default while closed, so the next open starts from
			// the design's own placement and only flips if it has to.
			setPlacement('down');
			return;
			}

		const measure = () => {
			const field = fieldRef.current;
			const panel = field?.querySelector<HTMLElement>(PANEL_SELECTOR);
			if (!field || !panel)
				return;

			// The gap between field and panel lives in whichever margin is
			// currently doing the work -- margin-top while down, margin-bottom
			// once flipped -- and the other is zeroed, so max() reads it
			// without this needing to know which way round it already is.
			const style = getComputedStyle(panel);
			const offset = Math.max(parseFloat(style.marginTop) || 0, parseFloat(style.marginBottom) || 0);
			const needed = panel.offsetHeight + offset;

			// Measured off the FIELD, not the panel: the field's edges are the
			// two anchors the panel can hang from, and they don't move when
			// the placement does -- so this can't oscillate between states.
			const rect = field.getBoundingClientRect();
			const below = window.innerHeight - rect.bottom;
			const above = rect.top;

			// Flip only when down genuinely doesn't fit AND up is the roomier
			// side. The second test matters when neither side fits (a short
			// window, a tall calendar): staying down is then the better of two
			// bad placements, since the page can at least be scrolled.
			setPlacement(below < needed && above > below ? 'up' : 'down');
			};

		measure();

		// Coalesced to a frame, same shape as initSticky()'s own scroll
		// handler in index.tsx -- scroll fires far more often than the browser
		// paints, and every measure() reads layout.
		let ticking = false;

		const onScroll = () => {
			if (ticking)
				return;
			ticking = true;
			window.requestAnimationFrame(() => {
				ticking = false;
				measure();
				});
			};

		// The bar moves under the viewport as the page scrolls -- including
		// detaching to the bottom of the screen mid-open, if it's sticky -- so
		// an open panel re-measures rather than keeping the placement it was
		// given when it opened.
		window.addEventListener('scroll', onScroll, { passive: true });
		window.addEventListener('resize', onScroll, { passive: true });

		// The panel's own height is not fixed for as long as it's open: the
		// calendar's Apply/flexibility row appears once a range is picked, and
		// a longer month grid is taller. Re-measure on the panel itself rather
		// than assuming the height it had at open time.
		const panel = fieldRef.current?.querySelector<HTMLElement>(PANEL_SELECTOR);
		const observer = panel ? new ResizeObserver(measure) : null;
		if (panel && observer)
			observer.observe(panel);

		return () => {
			window.removeEventListener('scroll', onScroll);
			window.removeEventListener('resize', onScroll);
			observer?.disconnect();
			};
		}, [isOpen, fieldRef]);

	return placement;
	};
